/**
 * Simplified IST date utility functions
 * Only includes functions that are actually used in the codebase
 */

// IST timezone offset constant (UTC+5:30)
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000

/**
 * Format time string (HH:MM) to 12-hour format with AM/PM
 * @param timeString - Time string in HH:MM format (24-hour)
 * @returns Formatted time string with AM/PM
 */
export function formatTimeWithAMPM(timeString: string): string {
  const [hours, minutes] = timeString.split(':').map(Number)
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`
}

/**
 * Format time range (HH:MM - HH:MM) to 12-hour format with AM/PM
 * @param startTime - Start time string in HH:MM format
 * @param endTime - End time string in HH:MM format
 * @returns Formatted time range string
 */
export function formatTimeRangeWithAMPM(startTime: string, endTime: string): string {
  return `${formatTimeWithAMPM(startTime)} - ${formatTimeWithAMPM(endTime)}`
}

/**
 * Get current IST time as a Date object
 * @returns Current IST time
 */
function getCurrentISTTime(): Date {
  const now = new Date()
  return new Date(now.getTime() + (now.getTimezoneOffset() * 60 * 1000) + IST_OFFSET_MS)
}

/**
 * Convert a date string (YYYY-MM-DD) to IST Date object
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object in IST timezone
 */
export function parseISTDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day) // month is 0-indexed
}

/**
 * Get today's date in IST timezone
 * @returns Date object representing today at 00:00:00 IST
 */
export function getTodayIST(): Date {
  const currentIST = getCurrentISTTime()
  return new Date(currentIST.getFullYear(), currentIST.getMonth(), currentIST.getDate())
}

/**
 * Format a Date object to IST date string (YYYY-MM-DD)
 * @param date - Date object to format
 * @returns Formatted date string
 */
export function formatISTDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Add days to a date string and return new date string
 * @param dateString - Date string in YYYY-MM-DD format
 * @param days - Number of days to add
 * @returns New date string
 */
export function addDaysIST(dateString: string, days: number): string {
  const date = parseISTDate(dateString)
  date.setDate(date.getDate() + days)
  return formatISTDate(date)
}

/**
 * Get display date for IST (e.g., "Today", "Tomorrow", or formatted date)
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Display string
 */
export function getDisplayDateIST(dateString: string): string {
  const date = parseISTDate(dateString)
  const today = getTodayIST()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  
  if (date.getTime() === today.getTime()) {
    return 'Today'
  } else if (date.getTime() === tomorrow.getTime()) {
    return 'Tomorrow'
  } else {
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }
}

/**
 * Get ordinal suffix for a day number
 * @param day - Day number (1-31)
 * @returns Ordinal suffix (st, nd, rd, th)
 */
function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th'
  }
  switch (day % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

/**
 * Format date string to readable format with separate parts for React components
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Object with day, suffix, month, and year
 */
export function formatReadableDateParts(dateString: string): {
  day: number
  suffix: string
  month: string
  year: number
} {
  const date = parseISTDate(dateString)
  const day = date.getDate()
  const month = date.toLocaleDateString('en-US', { month: 'long' })
  const year = date.getFullYear()
  
  return {
    day,
    suffix: getOrdinalSuffix(day),
    month,
    year
  }
}

/**
 * Check if a schedule is in the future (after current IST time)
 * @param specificDate - Date string in YYYY-MM-DD format
 * @param startTime - Time string in HH:MM format
 * @returns True if schedule is in the future
 */
export function isScheduleInFuture(specificDate: string, startTime: string): boolean {
  // Create schedule date in IST timezone
  const [year, month, day] = specificDate.split('-').map(Number)
  const [hours, minutes] = startTime.split(':').map(Number)
  
  // Create date in IST (UTC+5:30)
  const scheduleDateTime = new Date(year, month - 1, day, hours, minutes)
  
  // Get current IST time
  const currentIST = getCurrentISTTime()
  
  return scheduleDateTime > currentIST
}

/**
 * Check if a date is today or in the future
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns True if date is today or future
 */
export function isTodayOrFuture(dateString: string): boolean {
  const date = parseISTDate(dateString)
  const today = getTodayIST()
  return date >= today
}

/**
 * Get minimum date (today) for date inputs
 * @returns Today's date in YYYY-MM-DD format
 */
export function getMinDateIST(): string {
  return formatISTDate(getTodayIST())
}

/**
 * Compare two IST dates
 * @param date1 - First date string
 * @param date2 - Second date string
 * @returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export function compareISTDates(date1: string, date2: string): number {
  const d1 = parseISTDate(date1)
  const d2 = parseISTDate(date2)
  return d1.getTime() - d2.getTime()
}

/**
 * Compare two time strings
 * @param time1 - First time string (HH:MM)
 * @param time2 - Second time string (HH:MM)
 * @returns -1 if time1 < time2, 0 if equal, 1 if time1 > time2
 */
export function compareTimesIST(time1: string, time2: string): number {
  const [h1, m1] = time1.split(':').map(Number)
  const [h2, m2] = time2.split(':').map(Number)
  const minutes1 = h1 * 60 + m1
  const minutes2 = h2 * 60 + m2
  return minutes1 - minutes2
}

/**
 * Get minimum time for today (current IST time + 1 hour)
 * @returns Current IST time + 1 hour in HH:MM format
 */
export function getMinTimeIST(): string {
  const currentIST = getCurrentISTTime()
  
  // Add 1 hour to current IST time
  const oneHourLater = new Date(currentIST.getTime() + 60 * 60 * 1000)
  
  const hours = String(oneHourLater.getHours()).padStart(2, '0')
  const minutes = String(oneHourLater.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * Validate if a time is valid for a given date (must be in the future for today)
 * @param dateString - Date string in YYYY-MM-DD format
 * @param timeString - Time string in HH:MM format
 * @returns True if time is valid for the date
 */
export function isValidScheduleTime(dateString: string, timeString: string): boolean {
  const today = getMinDateIST()
  
  // If it's today, check if time is at least 1 hour in the future
  if (dateString === today) {
    const minTime = getMinTimeIST()
    return compareTimesIST(timeString, minTime) >= 0
  }
  
  // For future dates, any time is valid
  return isTodayOrFuture(dateString)
}
